/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';

import { cssMap, css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { jsx, css } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Text, Inline } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Breakpoint } from '../../hooks/useElementBreakpoints';

import messages from './messages';

const buttonContainerStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.100'),
});

const cmdPaletteButtonContainerStyles = cssMap({
	container: {
		display: 'flex',
		flexWrap: 'wrap',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: { height: token('space.400'), alignItems: 'center', borderRadius: token('space.050') },
	},
	primaryButton: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: { border: `${token('border.width')} solid ${token('color.border')}` },
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const divWrapperContainerStyleUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	gap: '10px',
});

const divWrapperContainerStyle = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'flex-start',
	alignItems: 'center',
	flex: 1,
});

const smallBreakPointAlignItems = css({ alignItems: 'flex-start' });

const errorFloatingContainerStyle = css({
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100'),
});

const smallErrorFloatingContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const spanWrapperStyleUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
});

const spanWrapperStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
	flexShrink: 0,
	// 2px top offset at small breakpoints for better icon-text alignment
	top: token('space.0'),
});

const smallBreakPointTop = css({ top: token('space.negative.025') });

const dangerIconPaddingStyle = css({
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.050'),
});

export function ErrorMessage({
	children,
	breakpoint,
	isAIFC,
}: {
	breakpoint: Breakpoint;
	children: React.ReactNode;
	isAIFC?: boolean;
}): JSX.Element {
	const { formatMessage } = useIntl();
	return (
		<div
			css={[
				divWrapperContainerStyle,
				divWrapperContainerStyleUnbounded,
				breakpoint <= Breakpoint.Small && smallBreakPointAlignItems,
			]}
		>
			<span
				css={[
					spanWrapperStyle,
					spanWrapperStyleUnbounded,
					breakpoint <= Breakpoint.Small && smallBreakPointTop,
				]}
			>
				{isAIFC ? (
					<span css={dangerIconPaddingStyle}>
						<StatusErrorIcon
							label={formatMessage(messages.warningIconLabel)}
							color={token('color.icon.danger')}
						/>
					</span>
				) : (
					<WarningIcon
						label={formatMessage(messages.warningIconLabel)}
						color={token('color.icon.warning')}
					/>
				)}
			</span>

			<Text as="p">{children}</Text>
		</div>
	);
}

export function ErrorButton({
	children,
	onClick,
	appearance,
	testId,
	autoFocus,
	disabled = false,
	isAIFC,
}: {
	appearance: 'subtle' | 'primary';
	autoFocus?: boolean;
	children: React.ReactNode;
	disabled?: boolean;
	isAIFC?: boolean;
	onClick: () => void;
	testId?: string;
}): JSX.Element {
	const shouldUseSubtleStyle = isAIFC && appearance === 'primary';
	const finalAppearance = shouldUseSubtleStyle ? 'subtle' : appearance;
	const button = (
		<Button
			autoFocus={autoFocus}
			testId={testId}
			spacing="compact"
			onClick={onClick}
			type="button"
			appearance={finalAppearance}
			isDisabled={disabled}
		>
			{children}
		</Button>
	);
	if (shouldUseSubtleStyle) {
		return <Inline xcss={cmdPaletteButtonContainerStyles.primaryButton}>{button}</Inline>;
	}

	return button;
}

export function ErrorButtonContainer({
	children,
	isAIFC,
}: {
	children: React.ReactNode;
	isAIFC?: boolean;
}): JSX.Element {
	return isAIFC ? (
		<Inline xcss={cmdPaletteButtonContainerStyles.container}>{children}</Inline>
	) : (
		<div css={buttonContainerStyles}>{children}</div>
	);
}

// Need to use forwardRef because functional component cannot be passed a ref
// as a prop.
export const ErrorFloatingContainer: ForwardRefExoticComponent<
	{
		appearance?: EditorAppearance;
		breakpoint: Breakpoint;
		children: React.ReactNode;
		isAIFC?: boolean;
	} & RefAttributes<HTMLDivElement>
> = forwardRef(
	(
		{
			children,
			breakpoint,
			appearance,
			isAIFC,
		}: {
			appearance?: EditorAppearance;
			breakpoint: Breakpoint;
			children: React.ReactNode;
			isAIFC?: boolean;
		},
		ref: React.Ref<HTMLDivElement>,
	) => {
		return (
			<FloatingContainer
				ref={ref}
				data-testid="error-screen"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`error-screen ${AI_MODAL_SCREEN_CLASS}`}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				css={[
					errorFloatingContainerStyle,
					breakpoint <= Breakpoint.Small && smallErrorFloatingContainerStyle,
				]}
				paddingAdjustments={
					isAIFC
						? appearance === 'chromeless' &&
							expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
							? { right: 0, left: 0 }
							: { right: 4, left: 4 }
						: { top: 1, bottom: 1 }
				}
				rainbowBorder={false}
				appearance={appearance}
				isAIFC={isAIFC}
			>
				{children}
			</FloatingContainer>
		);
	},
);
